import * as React from 'react';
import styled from 'styled-components';
import { formatStatementDate } from 'utils/formatting';
import t from 'utils/translations';
import { KeyFigureData, StatementKeyFigures } from 'components/context/types';
import Loading from 'components/common/Loading';
import { formatThousandSpacing } from 'utils/formatting';

import {
    GraphDataSet,
    KeyFigureHighchart
} from 'components/common/KeyFigureGraph/KeyFigureHighchart';

const NoDataInfo = styled.p`
    text-align: center;
    margin: 1em 0;
`;

const Footnote = styled.span`
    position: relative;
    float: right;

    font-size: 12px;
    right: 8px;
    bottom: 20px;
`;

const buildSecondaryGraphData = (
    keyFigureData: KeyFigureData,
    selectedField: string,
    categories: string[],
    isOldData: boolean,
    isPrimaryAggregated: boolean
) => {
    const secondaryGraphData = {
        seriesName: isOldData
            ? keyFigureData.seriesName + '*'
            : keyFigureData.seriesName,
        data: [] as number[]
    };

    const secondaryKeyFigures = keyFigureData.keyFigures;

    secondaryGraphData.data = categories.map(year => {
        const categoryYear = parseCategoryYear(year, isPrimaryAggregated);
        const yearData = secondaryKeyFigures.find(dataPoint => {
            const dataPointYear = dataPoint.statementDate.split('-')[0];
            return dataPointYear === categoryYear;
        });
        if (yearData && yearData[selectedField]) {
            return yearData[selectedField];
        }
        return null;
    });

    return secondaryGraphData;
};

function parseCategoryYear(
    categoryString: string,
    isAggregateCategory: boolean
): string {
    return isAggregateCategory
        ? categoryString.substring(0, 4)
        : categoryString.split('/')[1];
}

const getFirstStatementsDate = (keyFigures: StatementKeyFigures[]): string => {
    const length = keyFigures.length;
    if (length > 0) {
        return keyFigures[0].statementDate;
    }
    return '';
};

const getLastStatementsDate = (keyFigures: StatementKeyFigures[]): string => {
    const length = keyFigures.length;
    if (length > 0) {
        return keyFigures[length - 1].statementDate;
    }
    return '';
};

const renderOldDataFootnote = (keyFigures: StatementKeyFigures[]) => {
    const latestStatementDate = getLastStatementsDate(keyFigures);
    if (latestStatementDate === '') {
        return <Footnote>*{t('COMPARE_NO_STATEMENTS')}</Footnote>;
    }

    return (
        <Footnote>
            *
            {`${t('COMPARE_LATEST_STATEMENT')}: ${formatStatementDate(
                latestStatementDate
            )}`}
        </Footnote>
    );
};

interface Props {
    showMonths: boolean;
    isAggregateData?: boolean;
    isLoading: boolean;
    // NOTE: The data sets need to be already sorted by year (ascended)
    keyFigureSuffix: string;
    primaryDataSet: KeyFigureData;
    secondaryDataSets: KeyFigureData[];
    selectedKeyFigureField: string;
    sampleSizeField: string;
    title: string;
    yAxisLabel: string;
}

export const KeyFigureGraphBody = ({
    isAggregateData,
    showMonths,
    isLoading,
    keyFigureSuffix,
    primaryDataSet,
    secondaryDataSets,
    selectedKeyFigureField,
    sampleSizeField,
    title,
    yAxisLabel
}: Props) => {
    const isPrimaryDataAvailable =
        !isLoading && primaryDataSet.keyFigures.length > 0;

    const constructGraph = () => {
        const categories: string[] = [];
        const primaryGraphData: GraphDataSet = {
            seriesName: primaryDataSet.seriesName,
            data: []
        };
        primaryDataSet.keyFigures.forEach(dataPoint => {
            const selectedKeyFigureData = dataPoint[selectedKeyFigureField];
            dataPoint[sampleSizeField]
                ? categories.push(
                      dataPoint.statementDate.split('-')[0] +
                          '<br/> <br/>' +
                          t('SAMPLE_SIZE') +
                          formatThousandSpacing(dataPoint[sampleSizeField])
                  )
                : !isAggregateData
                ? categories.push(
                      showMonths
                          ? formatStatementDate(dataPoint.statementDate)
                          : dataPoint.statementDate.split('-')[0]
                  )
                : categories.push(
                      dataPoint.statementDate.split('-')[0] +
                          '<br/> <br/>' +
                          t('SAMPLE_SIZE') +
                          0
                  );

            // NOTE: the HiChart graph handles null values but not undefined
            primaryGraphData.data.push(
                selectedKeyFigureData !== null &&
                    selectedKeyFigureData !== undefined
                    ? selectedKeyFigureData
                    : null
            );
        });

        let isOldData = false;
        const secondaryGraphDataSets: GraphDataSet[] = secondaryDataSets.map(
            (secondaryDataSet: KeyFigureData) => {
                isOldData =
                    getLastStatementsDate(secondaryDataSet.keyFigures) <
                    getFirstStatementsDate(primaryDataSet.keyFigures);

                return buildSecondaryGraphData(
                    secondaryDataSet,
                    selectedKeyFigureField,
                    categories,
                    isOldData,
                    isAggregateData === true
                );
            }
        );

        const columnProps = {
            categories,
            dataSets: [primaryGraphData, ...secondaryGraphDataSets],
            height: 325,
            suffix: keyFigureSuffix,
            title,
            yAxisLabel
        };
        return (
            <>
                <KeyFigureHighchart {...columnProps} />
                {isOldData &&
                    renderOldDataFootnote(secondaryDataSets[0].keyFigures)}
            </>
        );
    };

    return isLoading ? (
        <Loading fullScreen={false} height={325} />
    ) : (
        <>
            {isPrimaryDataAvailable ? (
                constructGraph()
            ) : (
                <NoDataInfo>{t('NO_STATEMENT_HISTORY_KF')}</NoDataInfo>
            )}
        </>
    );
};
